/**
 * What this phone can actually show.
 *
 * A restricted user's snapshot genuinely does not contain the ledgers behind
 * the Balance Sheet, so those screens would render as a row of zeroes — which
 * reads as "the business has no cash" rather than "you were not given this".
 * Silence would be worse than a refusal.
 *
 * DECIDED FROM THE DATA, not from the role name. The role label is only ever a
 * caption; if a future role withholds something different, this keeps working
 * without being taught about it. And it cannot be gamed: a screen hidden here
 * is hidden because the figures are not on the device, so unhiding it would
 * reveal nothing.
 */
import type { Snapshot } from "@tally/shared";

import type { RouteId } from "./routes.js";

export type ViewLimits = {
  /** the role's caption, or null on a full snapshot */
  label: string | null;
  hidden: Set<RouteId>;
  /** shown once, on the screens that lost the most */
  note: string | null;
};

const MONEY_GROUPS = /cash-in-hand|bank/i;

/**
 * Does this copy still carry the cash and bank ledgers?
 *
 * Exported because the Graphs tab asks the same question: its Financial charts
 * are built from the same ledgers as the Balance Sheet and P&L, so a redacted
 * copy that hides those screens must not be able to draw them instead. One
 * definition, or the two answers drift apart the day a role changes.
 */
export function hasMoneyLedgers(snapshot: Snapshot): boolean {
  return snapshot.ledgers.some((ledger) => MONEY_GROUPS.test(ledger.parent));
}

export function viewLimits(snapshot: Snapshot | null): ViewLimits {
  const none: ViewLimits = { label: null, hidden: new Set(), note: null };
  if (!snapshot?.role) return none;

  const hidden = new Set<RouteId>();

  // No cash or bank ledger means no meaningful Balance Sheet, Trial Balance,
  // Cash Book or Ratios — each of those is mostly made of the accounts that
  // were withheld.
  if (!hasMoneyLedgers(snapshot)) {
    hidden.add("balance-sheet");
    hidden.add("trial-balance");
    hidden.add("cash-book");
    hidden.add("ratios");
    hidden.add("profit-loss");
    hidden.add("reports");
  }

  if (snapshot.stockItems.length === 0) {
    hidden.add("stock");
    // Both of these are built from item lines; with no stock there is nothing
    // to rank and nothing to call dead.
    hidden.add("top-items");
    hidden.add("dead-stock");
  }

  // Cost centres cannot be reported on when the allocations were stripped.
  if (snapshot.costCentres.length === 0) hidden.add("cost-centres");

  return {
    label: snapshot.role.label,
    hidden,
    note:
      hidden.size > 0
        ? `You are using the ${snapshot.role.label} view. Screens that need figures ` +
          `outside it are not shown — those figures are not on this phone.`
        : null,
  };
}
